import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import MovieCard from "@/components/feature/MovieCard";
import EpisodeList from "@/pages/detail/components/EpisodeList";
import { useFavorites } from "@/hooks/useFavorites";
import { getMovieById, getRelated, typeLabel } from "@/lib/movieData";

export default function Detail() {
  const { id } = useParams();
  const movie = getMovieById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  usePageMeta({
    title: movie ? `${movie.title} - 最近中文字幕` : "作品未找到 - 最近中文字幕",
    description: movie
      ? `${movie.title}（${movie.year}，${movie.region}）：${movie.summary} 提供中文字幕更新信息与作品简介。`
      : "未找到对应的影视作品信息。",
    keywords: movie
      ? `最近中文字幕,${movie.title},${movie.genres.join(",")},中文字幕,影视简介`
      : "最近中文字幕",
  });

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!movie) {
    return (
      <div className="w-full px-4 md:px-6 py-24 text-center">
        <i className="ri-file-unknow-line text-5xl text-foreground-300"></i>
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">
          作品未找到
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          该作品可能已被移除，去首页看看最新更新吧。
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 dark:text-foreground-950 whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const related = getRelated(movie, 4);
  const favorited = isFavorite(movie.id);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("链接已复制，可分享给朋友");
    } catch {
      setToast("复制失败，请手动复制地址栏链接");
    }
  };

  return (
    <div className="w-full px-4 md:px-6 py-8 md:py-10">
      <nav className="mb-6 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="hover:text-primary-700">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to={`/category/${movie.type}`} className="hover:text-primary-700">
          {typeLabel(movie.type)}
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-900">{movie.title}</span>
      </nav>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[320px_1fr]">
        <div className="mx-auto w-52 sm:w-64 lg:mx-0 lg:w-full">
          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
            <img
              src={movie.poster}
              alt={`${movie.title}中文字幕海报`}
              title={`${movie.title} 中文字幕`}
              className="aspect-[2/3] w-full object-cover object-top"
            />
          </div>
        </div>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-primary-600 px-2.5 py-1 text-xs font-medium text-background-50 dark:text-foreground-950">
              {typeLabel(movie.type)}
            </span>
            {movie.genres.map((genre) => (
              <Link
                key={genre}
                to={`/category/${movie.type}?genre=${encodeURIComponent(genre)}`}
                className="rounded-full border border-secondary-200 bg-secondary-50 px-3 py-1 text-xs text-secondary-900 transition-colors hover:border-accent-300 hover:bg-accent-100"
              >
                {genre}
              </Link>
            ))}
          </div>

          <h1 className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
            {movie.title}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-foreground-600">
            <span className="flex items-center gap-1">
              <i className="ri-star-fill text-accent-500"></i>
              <strong className="text-foreground-950">{movie.rating.toFixed(1)}</strong> 分
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-accent-500"></i>
              {(movie.views / 10000).toFixed(1)} 万热度
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              字幕更新于 {movie.updatedAt}
            </span>
          </div>

          <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 border-y border-background-200 py-5 sm:grid-cols-2">
            <div className="flex gap-3 text-sm">
              <dt className="w-14 shrink-0 text-foreground-500">年代</dt>
              <dd className="text-foreground-900">{movie.year} 年</dd>
            </div>
            <div className="flex gap-3 text-sm">
              <dt className="w-14 shrink-0 text-foreground-500">地区</dt>
              <dd className="text-foreground-900">{movie.region}</dd>
            </div>
            <div className="flex gap-3 text-sm">
              <dt className="w-14 shrink-0 text-foreground-500">导演</dt>
              <dd className="text-foreground-900">{movie.director}</dd>
            </div>
            <div className="flex gap-3 text-sm">
              <dt className="w-14 shrink-0 text-foreground-500">主演</dt>
              <dd className="text-foreground-900">{movie.cast.join(" / ")}</dd>
            </div>
          </dl>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => toggleFavorite(movie.id)}
              className={`inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold transition-colors whitespace-nowrap ${
                favorited
                  ? "bg-accent-500 text-background-50 dark:text-foreground-950"
                  : "border border-background-300 text-foreground-700 hover:border-accent-300 hover:text-accent-600"
              }`}
            >
              <i className={favorited ? "ri-bookmark-3-fill" : "ri-bookmark-3-line"}></i>
              {favorited ? "已收藏" : "收藏"}
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 whitespace-nowrap"
            >
              <i className="ri-share-forward-line"></i>
              分享
            </button>
          </div>
        </div>
      </div>

      {/* 简介模块 */}
      <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <article className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6 lg:col-span-2">
          <div className="mb-3 flex items-center gap-2">
            <i className="ri-file-text-line text-primary-600 text-lg"></i>
            <h2 className="font-heading text-lg font-bold text-foreground-950">作品简介</h2>
          </div>
          <p className="text-sm leading-relaxed text-foreground-600 md:text-base">
            {movie.synopsis}
          </p>
        </article>

        <aside className="rounded-lg border border-accent-200 bg-accent-50 p-5 md:p-6">
          <div className="mb-3 flex items-center gap-2">
            <i className="ri-translate-2 text-accent-600 text-lg"></i>
            <h2 className="font-heading text-lg font-bold text-foreground-950">中文字幕信息</h2>
          </div>
          <p className="text-sm leading-relaxed text-foreground-700">{movie.subtitleNote}</p>
          <p className="mt-4 flex items-start gap-2 text-xs text-foreground-500">
            <i className="ri-information-line mt-0.5"></i>
            字幕信息以页面展示为准，本站不提供字幕文件下载。
          </p>
        </aside>
      </section>

      {/* 剧集列表（仅电视剧） */}
      {movie.type === "tv" && movie.episodes && movie.episodes.length > 0 && (
        <section className="mt-8">
          <EpisodeList episodes={movie.episodes} />
        </section>
      )}

      {/* 相关推荐 */}
      {related.length > 0 && (
        <section className="mt-10">
          <div className="mb-5 flex items-center gap-2">
            <i className="ri-magic-line text-accent-600 text-lg"></i>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              相关推荐
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
            {related.map((item) => (
              <MovieCard key={item.id} movie={item} />
            ))}
          </div>
        </section>
      )}

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 rounded-md bg-foreground-950 px-4 py-2.5 text-sm text-background-50 animate-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}